import { useState, useRef } from "react";
import SparkMD5 from "spark-md5";
import { api } from "../api";
import { Card, Field, Input, Btn, ProgressBar } from "../components/Shared";
import {
  FileDropZone, OrderPicker, useTransferOrder, choosePartSize, hashSlice,
  partsToSend, withRetries, fmtBytes, MAX_FILE_BYTES,
} from "./SubmissionWizard";

/**
 * Sending delivery data into a project from the browser.
 *
 * Everything about how the bytes move is imported from the submission wizard
 * rather than written again. That is deliberate: the part sizing, the local-file
 * MD5, the replay-safe retry and the folder-refusing dropzone each exist because
 * of a specific incident, and none of them is obvious from reading the happy
 * path. A second implementation would repeat all of them.
 *
 * The one thing that is different is the destination. The operator types only
 * the last segment of the path; the prefix in front of it is fixed and shown
 * read-only, so a delivery cannot be aimed at another project by mistyping.
 *
 * **`order` in this file is an identifier and nothing else.** `order_id` and
 * `orderChoice` keep the backend's names — renaming a route is a contract
 * change for one word — while every sentence on screen says *quotation*, which
 * is what the piece of agreed work is called (the facility, 2026-09-16).
 */
export default function DeliveryUploadPanel({ project, onDone }) {
  const [files, setFiles]       = useState([]);
  const [subfolder, setSub]     = useState("");
  const [busy, setBusy]         = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError]       = useState(null);
  const [done, setDone]         = useState(null);
  const xhrRef  = useRef(null);
  const jobRef  = useRef(null);

  // A delivery belongs to a quotation for the same reason an upload does — the
  // quotation IS the piece of agreed work, and it is what the record sent at
  // the end names. Same hook, same rule: one open quotation and nothing is
  // asked, several and the operator picks.
  const orderChoice = useTransferOrder(project.id);

  const prefix = project.results_source_path || "";
  const totalBytes = files.reduce((n, f) => n + f.file.size, 0);
  const nameOk = /^[A-Za-z0-9._\-/]+$/.test(subfolder.trim());

  // The server decides whether this project may take data at all — an unsigned
  // quotation or an unconfirmed blocking document closes it — and it publishes
  // the answer with the project. Asking it here rather than re-deriving it is
  // the whole point of _transfer_readiness: two implementations of "is this
  // project ready" is how a screen comes to offer a button the server refuses.
  //
  // **Honoured with no exception, and there was one for a few hours on
  // 2026-09-16.** It read `... && !orderChoice.mustChoose`, written while
  // `_transfer_readiness` still called the gate ONCE with no quotation named —
  // so a project with several live quotations always answered false carrying
  // "choose the quotation", a question rather than a refusal. That function now
  // asks the gate once per live quotation and answers true if any of them would
  // take data (portal/services/projects.py), so `false` again means every one
  // of them refused: an unsigned quotation, a deadline gone, a document not
  // confirmed. Ignoring it while a choice is outstanding hid the real reason
  // and enabled Upload straight into the 409 this panel exists to prevent.
  const blocked = project.can_transfer_data === false;

  // Why the button is dead, in words. Every one of these used to be silence:
  // the operator picked files, typed a folder, pressed Upload and got a 409 —
  // or, worse, nothing at all.
  const why =
    blocked ? (project.transfer_blocked_reason || "This project cannot take data yet.")
    : orderChoice.blockedBy ? orderChoice.blockedBy
    : files.length === 0 ? "Choose the files to upload."
    : !subfolder.trim() ? "Give this batch a folder name."
    : !nameOk ? "Letters, digits, dot, dash, underscore and / only — no spaces."
    : null;

  const ready = !why && !busy;

  const upload = async () => {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      const oversize = files.find(f => f.file.size > MAX_FILE_BYTES);
      if (oversize) throw new Error(`${oversize.file.name} is over the 5 TiB limit for one object.`);

      // `order_id` is a query parameter on results/upload/start, and empty
      // unless the operator picked one — a project with a single open quotation
      // lets the server decide at the moment the upload starts.
      //
      // The picker is a convenience, not the guarantee: the project can change
      // between this card loading and Upload being pressed, and the server says
      // so — 409 naming every open quotation when none was named, 404 when the
      // named one has since closed. That sentence is shown as it stands, and
      // the list is re-read so the picker describes the project as it is now.
      // Only here — nothing re-checks once bytes are moving.
      let start;
      try {
        start = await api.post(
          `/api/projects/${project.id}/results/upload/start${orderChoice.query}`, {
            subfolder: subfolder.trim(),
            files: files.map(f => ({ name: f.file.name, size: f.file.size })),
          });
      } catch (e) {
        // **404 as well as 409, and the 404 is the one the picker causes.**
        // 409 is the server asking which quotation, which happens when none was
        // named. Once one HAS been named and it closed in the meantime — the
        // sweep ran, somebody cancelled it — `_order_for_transfer` answers 404
        // ("not one of this project's, or it is no longer open"), and recovering
        // only from 409 left the picker holding a dead choice with no way to
        // refresh it. Reloading on either is safe: it re-reads the list, and a
        // 404 that was really about the project shows the same refusal again.
        if (e?.status === 409 || e?.status === 404) await orderChoice.reload();
        throw e;
      }
      jobRef.current = start.job_row_id;

      let sent = 0;
      for (const f of files) {
        const partSize  = choosePartSize(f.file.size);
        const partCount = Math.max(1, Math.ceil(f.file.size / partSize));
        // The whole-file digest is fed by hashSlice as the parts go past, the
        // same way the submission wizard does it — one pass over the local file,
        // so the checksum is independent of what the server received.
        const wholeHash = new SparkMD5.ArrayBuffer();
        const fileBase  = sent;

        for (const n of partsToSend(partCount)) {
          const base = (n - 1) * partSize;
          const blob = f.file.slice(base, Math.min(base + partSize, f.file.size));
          const partMd5 = await hashSlice(blob, wholeHash);
          const headers = {
            "Content-Type": "application/octet-stream",
            "X-Filename": encodeURIComponent(f.file.name),
            "X-Job-Id": start.job_id,
            "X-Subfolder": encodeURIComponent(subfolder.trim()),
            "X-File-Size": String(f.file.size),
            "X-Part-Number": String(n),
            "X-Part-Count": String(partCount),
            "X-Part-MD5": partMd5,
          };
          if (n === partCount) headers["X-File-MD5"] = wholeHash.end();

          await withRetries(() => api.uploadPart(
            `/api/projects/${project.id}/results/upload/stream`,
            blob, headers,
            loaded => setProgress({ name: f.file.name, totalLoaded: fileBase + base + loaded,
                                    totalBytes }),
            xhr => { xhrRef.current = xhr; },
          ));
        }
        sent += f.file.size;
      }

      const finished = await api.post(`/api/projects/${project.id}/results/upload/finish`,
                                      { job_id: start.job_id });
      setDone(finished);
      setFiles([]);
      setSub("");
      // Or a later Cancel posts to the job that already finished.
      jobRef.current = null;
      if (onDone) onDone(finished);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      setProgress(null);
    }
  };

  const cancel = async () => {
    // Cancel server-side FIRST — that is what purges the partial batch — and
    // only then abort the request. The other order leaves the server unaware.
    if (jobRef.current) {
      try { await api.post(`/api/jobs/${jobRef.current}/cancel`, {}); } catch { /* best effort */ }
    }
    xhrRef.current?.abort();
  };

  return (
    <Card>
      <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700 }}>Upload delivery data</h3>
      <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
        Sends files into this project's delivery area. Nothing is packaged yet — use
        <strong> + Package Results</strong> below when the delivery is complete.
      </p>

      {/* Not while the project is blocked: with `can_transfer_data` false every
          live quotation has already refused this data, so there is nothing here
          worth picking — the server's sentence is what the operator needs. */}
      {orderChoice.mustChoose && !blocked && (
        <div style={{ marginBottom: 16 }}>
          <OrderPicker
            orders={orderChoice.live}
            value={orderChoice.chosen}
            onChange={orderChoice.setChosen}
            disabled={busy}
          />
        </div>
      )}

      {error && <div role="alert" style={{ marginBottom: 12, color: "#dc2626", fontSize: 14 }}>{error}</div>}
      {done && (
        <div role="status" style={{ marginBottom: 12, color: "#065f46", fontSize: 14 }}>
          {done.files} file(s) uploaded to <code>{done.prefix}</code>.
        </div>
      )}

      <Field
        label="Folder for this batch"
        required
        hint="Everything in one folder is packaged into a single archive, so give each batch its own."
      >
        {/* The prefix is shown, fixed, immediately before the input: the
            operator can see the whole path they are creating, and can only
            change the part that is theirs to choose. */}
        <div style={{ display: "flex", alignItems: "stretch", flexWrap: "wrap" }}>
          <span
            style={{ display: "flex", alignItems: "center", padding: "10px 12px",
                     background: "#f3f4f6", border: "1px solid #d1d5db", borderRight: "none",
                     borderRadius: "8px 0 0 8px", fontFamily: "'JetBrains Mono', monospace",
                     fontSize: 13, color: "#374151", overflowWrap: "anywhere" }}
          >
            {prefix}/
          </span>
          <Input
            value={subfolder}
            onChange={e => setSub(e.target.value)}
            placeholder="2026-03-run1"
            disabled={busy}
            style={{ borderRadius: "0 8px 8px 0", flex: "1 1 180px", minWidth: 0 }}
          />
        </div>
      </Field>
      {subfolder.trim() && !nameOk && (
        <div style={{ marginTop: -8, marginBottom: 12, fontSize: 12, color: "#b45309" }}>
          Letters, digits, dot, dash, underscore and / only.
        </div>
      )}

      <FileDropZone files={files} onFiles={setFiles} />

      {progress && (
        <div style={{ marginTop: 16 }}>
          <ProgressBar value={progress.totalLoaded} max={progress.totalBytes} />
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 6 }}>
            {progress.name} · {fmtBytes(progress.totalLoaded)} of {fmtBytes(progress.totalBytes)}
          </div>
        </div>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 10, alignItems: "center" }}>
        <Btn onClick={upload} disabled={!ready}>
          {busy ? "Uploading…" : `Upload ${files.length || ""} file${files.length === 1 ? "" : "s"}`}
        </Btn>
        {busy && <Btn variant="secondary" onClick={cancel}>Cancel</Btn>}
        {files.length > 0 && !busy && (
          <span style={{ fontSize: 12, color: "#6b7280" }}>{fmtBytes(totalBytes)}</span>
        )}
        {why && !busy && (
          <span role="status" style={{ fontSize: 12, color: blocked ? "#92400e" : "#6b7280" }}>
            {why}
          </span>
        )}
      </div>
    </Card>
  );
}
